import { createWithEqualityFn } from "zustand/traditional";
import { persist, createJSONStorage } from "zustand/middleware";

import { User, UserPowerUps } from "../../../Common/models/user";
import * as capacitorStorage from "../../../Common/utils/localStorage";
import { LOCAL_STORAGE } from "../../../Common/config";
import { WLStripeWPlanSubscription } from "../../../Common/models/subscriptions";
import { CustomerInfo } from "@revenuecat/purchases-capacitor";
import { auth } from "../../firebase";

interface UserHeaderBanner {
  title: string;
  description: string;
  action?: {
    label: string;
    redirectTo?: string;
    onClick?: () => void;
  };
}

export type HeaderBannerSource = "billing" | "shop" | null;

interface UserState {
  user: User | null | undefined;
  setUser: (state: User, customerInfo: CustomerInfo | null) => void;
  customerInfo: CustomerInfo | null;

  isTokenResolved: boolean;
  setIsTokenResolved: (value: boolean) => void;

  headerBanner: null | UserHeaderBanner;
  headerBannerSource: HeaderBannerSource;
  setHeaderBanner: (
    header: null | UserHeaderBanner,
    source?: HeaderBannerSource,
  ) => void;

  hasBillingIssue: boolean;
  setHasBillingIssue: (state: boolean) => void;

  klaviyoInitialized: boolean;
  setKlaviyoInitialized: (state: boolean) => void;

  stripeActiveSubscription: null | WLStripeWPlanSubscription;
  setStripeActiveSubscription: (
    state: null | WLStripeWPlanSubscription
  ) => void;

  accumulatedEntries: number;
  setAccumulatedEntries: (state: number) => void;

  powerUpEntries: number;
  setPowerUpEntries: (state: number) => void;

  userPowerUps: UserPowerUps[];
  setUserPowerUps: (state: UserPowerUps[]) => void;

  userPowerUpsHistory: UserPowerUps[];
  setUserPowerUpsHistory: (state: UserPowerUps[]) => void;

  showSecureAccount: boolean;
  setShowSecureAccount: (showSecureAccount: boolean) => void;

  showCashPrizesAnnouncement: boolean;
  setShowCashPrizesAnnouncement: (showCashPrizesAnnouncement: boolean) => void;
  cashPrizesAnnouncementDismissed: boolean;
  setCashPrizesAnnouncementDismissed: (dismissed: boolean) => void;

  totalEntries: number;
  setTotalEntries: (state: number) => void;

  updateUser: (state: Partial<User>) => void;

  authProviders: string[];
  getProvider: () => string[] | [];

  removeUser: () => Promise<void>;
}

export const useUserStore = createWithEqualityFn<
  UserState,
  [["zustand/persist", UserState]]
>(
  persist(
    (set, get) => ({
      user: undefined as User | null | undefined,
      customerInfo: null as CustomerInfo | null,
      stripeActiveSubscription: null,
      headerBanner: null,
      headerBannerSource: null,
      setUser: (newState: User, customerInfo: CustomerInfo | null) => {
        const previousUser = get().user;

        const isNewLogin =
          !previousUser || previousUser.docId !== newState.docId;

        set(() => ({
          user: newState,
          customerInfo,
          showCashPrizesAnnouncement: isNewLogin
            ? true
            : get().showCashPrizesAnnouncement,
          cashPrizesAnnouncementDismissed: isNewLogin
            ? false
            : get().cashPrizesAnnouncementDismissed,
        }));
        const { firstName, lastName, emailAddress } = newState;
        capacitorStorage.setItem(
          LOCAL_STORAGE.userName,
          `${firstName} ${lastName}`
        );
        capacitorStorage.setItem(LOCAL_STORAGE.userEmail, emailAddress);
      },
      updateUser: (updateState: Partial<User>) => {
        const existing = get().user || ({} as User);
        return set(() => ({ user: { ...existing, ...updateState } }));
      },

      setStripeActiveSubscription: (stripeActiveSubscription) =>
        set({ stripeActiveSubscription }),
      removeUser: async () => {
        // console.log("removing user");
        return set(() => ({
          user: null,
          accumulatedEntries: 0,
          powerUpEntries: 0,
          userPowerUps: [],
          userPowerUpsHistory: [],
          totalEntries: 0,
          customerInfo: null,
          stripeActiveSubscription: null,
          showSecureAccount: true,
          showCashPrizesAnnouncement: true,
          cashPrizesAnnouncementDismissed: false,
          klaviyoInitialized: false,
        }));
      },

      isTokenResolved: false,
      setIsTokenResolved: (isTokenResolved) => set({ isTokenResolved }),

      setHeaderBanner: (headerBanner, source) => {
        const currentSource = get().headerBannerSource;
        if (headerBanner === null) {
          if (source !== undefined && currentSource !== source) {
            return;
          }
          set({ headerBanner: null, headerBannerSource: null });
          return;
        }
        set({
          headerBanner,
          headerBannerSource: source ?? null,
        });
      },

      klaviyoInitialized: false,
      setKlaviyoInitialized: (klaviyoInitialized) =>
        set({
          klaviyoInitialized,
        }),

      hasBillingIssue: false,

      showSecureAccount: true,
      setShowSecureAccount: (showSecureAccount: boolean) =>
        set({ showSecureAccount }),

      showCashPrizesAnnouncement: true,
      setShowCashPrizesAnnouncement: (showCashPrizesAnnouncement: boolean) =>
        set({ showCashPrizesAnnouncement }),
      cashPrizesAnnouncementDismissed: false,
      setCashPrizesAnnouncementDismissed: (dismissed: boolean) =>
        set({ cashPrizesAnnouncementDismissed: dismissed }),

      accumulatedEntries: 0,
      setAccumulatedEntries: (accumulatedEntries) =>
        set({ accumulatedEntries }),

      powerUpEntries: 0,
      setPowerUpEntries: (powerUpEntries) => set({ powerUpEntries }),

      userPowerUps: [],
      setUserPowerUps: (userPowerUps) => set({ userPowerUps }),

      userPowerUpsHistory: [],
      setUserPowerUpsHistory: (userPowerUpsHistory) =>
        set({ userPowerUpsHistory }),

      totalEntries: 0,
      setTotalEntries: (totalEntries) => set({ totalEntries }),

      setHasBillingIssue: (hasBillingIssue) =>
        set({
          hasBillingIssue,
        }),

      authProviders: [],
      getProvider: () => {
        const providers = auth.currentUser?.providerData.map(
          (p: any) => String(p.providerId) ?? ""
        );
        return providers || [];
      },
    }),
    {
      name: "user-detail-storage", // name of the item in the storage (must be unique)
      storage: createJSONStorage(() => sessionStorage), // (optional) by default, 'localStorage' is used
      partialize: (state) => ({
        ...state,
        headerBanner: null,
        headerBannerSource: null,
      }),
    }
  )
);

export const FETCH_USER_POWER_UPS_QUERY_KEY = "FETCH_USER_POWERUPS";
